import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, PencilLine, Star } from "lucide-react";
import api from "../api";
import { Reveal } from "./Reveal";

export const BookCover = ({ book, className = "aspect-[3/4]" }) => (
  <div
    className={`${className} rounded-2xl border-2 border-ink overflow-hidden flex items-center justify-center`}
    style={{ backgroundColor: book.color || "#A78BFA" }}
  >
    {book.cover ? (
      <img src={book.cover} alt={book.title} loading="lazy" className="w-full h-full object-cover" />
    ) : (
      <span className="text-6xl">{book.icon || "📖"}</span>
    )}
  </div>
);

export const BookRating = ({ book }) => {
  if (!book.rating || book.rating <= 0) return null;
  return (
    <div data-testid={`book-${book.id}-rating`} className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={15}
          className={i <= Math.round(book.rating) ? "text-sun" : "text-ink/20"}
          fill={i <= Math.round(book.rating) ? "#FFC72C" : "none"}
        />
      ))}
      <span className="text-xs font-bold text-ink/60 ml-1.5">
        {book.rating}
        {book.reviews_count
          ? ` · ${book.reviews_count} Amazon review${book.reviews_count === 1 ? "" : "s"}`
          : " on Amazon"}
      </span>
    </div>
  );
};

export const BookCard = ({ book }) => {
  const live = book.status === "live" && book.amazon_url;
  return (
    <motion.article
      data-testid={`book-card-${book.id}`}
      whileHover={{ x: -4, y: -4 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="bg-cream rounded-3xl border-2 border-ink shadow-brutal-sm hover:shadow-brutal p-5 flex flex-col gap-3.5 h-full"
    >
      <BookCover book={book} />
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full bg-sun border-2 border-ink">
          {book.age_range}
        </span>
        {book.price && (
          <span className="text-xs font-black px-3 py-1 rounded-full bg-mint/40 border-2 border-ink">
            {book.price}
          </span>
        )}
      </div>
      <h3 className="font-display font-bold text-lg sm:text-xl tracking-tight leading-snug">
        {book.title}
      </h3>
      <BookRating book={book} />
      {book.description && (
        <p className="text-sm text-ink/70 leading-relaxed flex-1">{book.description}</p>
      )}
      {live ? (
        <a
          href={book.amazon_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`book-${book.id}-buy-button`}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-[#FF9900] text-ink font-display font-extrabold border-2 border-ink shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-all duration-200"
        >
          <ShoppingCart size={18} /> Buy on Amazon
        </a>
      ) : (
        <span
          data-testid={`book-${book.id}-coming-soon`}
          className="inline-flex items-center gap-2 self-start text-xs font-black uppercase tracking-widest px-3.5 py-2 rounded-full bg-grape/15 text-ink border-2 border-dashed border-ink/40"
        >
          Coming soon
        </span>
      )}
    </motion.article>
  );
};

export const MysteryBookCard = () => (
  <motion.article
    data-testid="mystery-book-card"
    whileHover={{ x: -4, y: -4 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className="rounded-3xl border-2 border-dashed border-ink/40 bg-cream/50 p-5 flex flex-col items-center justify-center text-center gap-4 h-full min-h-[300px]"
  >
    <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-ink/40 bg-white flex items-center justify-center">
      <PencilLine size={28} className="text-ink/40" />
    </div>
    <h3 className="font-display font-bold text-xl sm:text-2xl tracking-tight text-ink/60">
      A new book is on the drawing board
    </h3>
    <p className="text-sm text-ink/50 leading-relaxed max-w-[250px]">
      More big, friendly pages are being sketched right now. Check back soon!
    </p>
    <span className="text-xs font-black uppercase tracking-widest px-3.5 py-2 rounded-full bg-sun/50 border-2 border-dashed border-ink/30 text-ink/60">
      Coming soon
    </span>
  </motion.article>
);

export const BooksSection = () => {
  const [books, setBooks] = useState([]);
  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data)).catch(() => {});
  }, []);

  if (books.length === 0) return null;

  return (
    <section id="books" data-testid="books-section" className="px-4 sm:px-8 py-20 sm:py-28 bg-white border-y-2 border-ink">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
            From the KITOE bookshelf
          </span>
          <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Colouring books on Amazon
          </h2>
          <p className="mt-4 text-base sm:text-lg text-ink/70 max-w-2xl leading-relaxed">
            Big, easy pages with bold outlines and friendly characters — screen-free fun
            by Keith B aka KITOE. Paperback, delivered by Amazon.
          </p>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {books.map((book, i) => (
            <Reveal key={book.id} delay={i * 0.08}>
              <BookCard book={book} />
            </Reveal>
          ))}
          <Reveal delay={books.length * 0.08}>
            <MysteryBookCard />
          </Reveal>
        </div>
        <Reveal delay={0.2} className="mt-10 text-center">
          <a
            href="https://www.amazon.co.uk/Keith-B-aka-KITOE/e/B0HN634F54"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="books-author-page-link"
            className="inline-flex items-center gap-2 font-bold border-b-2 border-coral text-ink hover:text-coral transition-colors"
          >
            See all books by Keith B aka KITOE on Amazon
          </a>
        </Reveal>
      </div>
    </section>
  );
};
